import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Bar, ComposedChart, LabelList } from 'recharts';
import { Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const RMProFormaStatements = ({ annualSummary, monthlySchedule, isLoading, error }) => {
  console.log('RMProFormaStatements props:', { annualSummary, monthlySchedule, isLoading, error });
  const axisTickStyle = { fill: 'hsl(var(--chart-axis))', fontSize: 12 };
  const legendFormatter = (value) => <span className="ace-chart-legend-text">{value}</span>;

  // Hooks must run before any early returns
  const alignedAnnual = React.useMemo(() => {
    const safeAnnual = Array.isArray(annualSummary) ? annualSummary : [];
    const startIndex = monthlySchedule?.[0]?.index || safeAnnual[0]?.index;
    if (!startIndex || safeAnnual.length === 0) return [];
    const start = parseISO(startIndex);
    const startMonth = start.getMonth();
    const baseYear = start.getFullYear();
    return safeAnnual.map((item, idx) => {
      const date = new Date(baseYear + idx, startMonth, 1);
      const roa = item.return_on_average_assets ?? (
        item.total_assets ? item.net_income / item.total_assets : null
      );
      const roe = item.return_on_average_equity ?? (
        item.total_equity ? item.net_income / item.total_equity : null
      );
      return {
        ...item,
        return_on_average_assets: roa,
        return_on_average_equity: roe,
        label: format(date, 'MMM yyyy'),
      };
    });
  }, [annualSummary, monthlySchedule]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
        <span className="ml-2 text-foreground">Loading Pro Forma Statements...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative rounded-xl border border-destructive/45 bg-destructive/15 px-4 py-3 text-destructive" role="alert">
        <strong className="font-bold">Error:</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return format(parseISO(dateString), 'MMM yyyy');
  };

  // alignedAnnual defined above

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return 'N/A';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
  };

  const formatPercentage = (value) => {
    if (value === undefined || value === null) return 'N/A';
    return `${(value * 100).toFixed(2)}%`;
  };

  const customTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const isIsoDateLabel = typeof label === 'string' && /\d{4}-\d{2}-\d{2}/.test(label);
      const headerLabel = isIsoDateLabel ? formatDate(label) : label;
      return (
        <div className="ace-tooltip p-4">
          <p className="font-bold text-foreground">{headerLabel}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.name}: ${entry.dataKey.includes('ratio') || entry.dataKey.includes('return') ? formatPercentage(entry.value) : formatCurrency(entry.value)}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const prepareMonthlyData = () => {
    return monthlySchedule.map(item => ({
      ...item,
      line_prepayment_amount: -Math.abs(item.line_prepayment_amount),
      loan_prepayment_amount: -Math.abs(item.loan_prepayment_amount),
      loan_scheduled_amortization: -Math.abs(item.loan_scheduled_amortization)
    }));
  };

  const renderChart = (title, dataKeys, yAxisFormatter) => (
    <div className="mb-6">
      <h3 className="mb-2 text-lg font-semibold text-primary">{title}</h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={alignedAnnual}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--chart-grid))" />
          <XAxis 
            dataKey="label" 
            stroke="hsl(var(--chart-axis))"
            tick={axisTickStyle}
          />
          <YAxis 
            yAxisId="left"
            stroke="hsl(var(--chart-axis))"
            tick={axisTickStyle}
            tickFormatter={yAxisFormatter} 
          />
          {title === "Key Ratios" && (
            <YAxis 
              yAxisId="right"
              orientation="right"
              stroke="hsl(var(--primary))"
              tick={axisTickStyle}
              tickFormatter={yAxisFormatter}
            />
          )}
          <Tooltip content={customTooltip} />
          <Legend formatter={legendFormatter} />
          {dataKeys.map((key, index) => (
            <Line 
              key={key.dataKey}
              type="monotone" 
              dataKey={key.dataKey} 
              stroke={key.color} 
              name={key.name} 
              yAxisId={key.dataKey === "efficiency_ratio" ? "right" : "left"}
              isAnimationActive={false}
              dot={false}
              strokeWidth={3}
              strokeDasharray={key.dashed ? "5 5" : "0"}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );

  const renderCombinedChart = () => {
    const dataAll = prepareMonthlyData();
    // Align to start of window and end at the last analysis period
    const start = parseISO(monthlySchedule[0].index);
    const end = parseISO(monthlySchedule[monthlySchedule.length - 1].index);
    const data = dataAll.filter(item => {
      const d = parseISO(item.index);
      return d >= start && d <= end;
    });
    const maxPositive = Math.max(...data.map(item => 
      item.loan_monthly_production + item.line_monthly_production + item.line_utilization_production
    ));
    const maxNegative = Math.min(...data.map(item => 
      item.line_prepayment_amount + item.loan_prepayment_amount + item.loan_scheduled_amortization
    ));
    const maxBalance = Math.max(...data.map(item => Math.max(item.line_balance, item.loan_balance)));

    return (
      <div className="mb-6">
        <h3 className="mb-2 text-lg font-semibold text-primary">Monthly Production and Balances</h3>
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--chart-grid))" />
            <XAxis
              dataKey="index"
              stroke="hsl(var(--chart-axis))"
              tick={axisTickStyle}
              tickFormatter={formatDate}
              interval={3}
              angle={-45}
              textAnchor="end"
              height={70}
            />
            <YAxis
              yAxisId="left"
              stroke="hsl(var(--chart-axis))"
              tick={axisTickStyle}
              tickFormatter={formatCurrency}
              domain={[maxNegative * 1.1, maxPositive * 1.1]}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke="hsl(var(--chart-axis))"
              tick={axisTickStyle}
              tickFormatter={formatCurrency}
              domain={[0, maxBalance * 1.1]}
            />
            <Tooltip content={customTooltip} />
            <Legend formatter={legendFormatter} />
            <Bar dataKey="loan_monthly_production" stackId="positive" fill="hsl(var(--chart-2))" name="Loan Monthly Production" yAxisId="left" />
            <Bar dataKey="line_monthly_production" stackId="positive" fill="hsl(var(--chart-3))" name="Line Monthly Production" yAxisId="left" />
            <Bar dataKey="line_utilization_production" stackId="positive" fill="hsl(var(--chart-2))" name="Line Utilization Production" yAxisId="left" />
            <Bar dataKey="line_prepayment_amount" stackId="negative" fill="hsl(var(--chart-down))" name="Line Prepayment Amount" yAxisId="left" />
            <Bar dataKey="loan_prepayment_amount" stackId="negative" fill="hsl(var(--chart-5))" name="Loan Prepayment Amount" yAxisId="left" />
            <Bar dataKey="loan_scheduled_amortization" stackId="negative" fill="hsl(var(--chart-down))" name="Loan Scheduled Amortization" yAxisId="left" />
            <Line type="monotone" dataKey="line_balance" stroke="hsl(var(--chart-3))" name="Line Balance" yAxisId="right" />
            <Line type="monotone" dataKey="loan_balance" stroke="hsl(var(--chart-1))" name="Loan Balance" yAxisId="right" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderKeyRatiosChart = () => {
    const CustomizedLabel = (props) => {
      const { x, y, width, height, value } = props;
      const fontSize = 12;
      const formattedValue = formatPercentage(value);
      const textWidth = formattedValue.length * fontSize * 0.6; // Approximate text width
      const padding = 4;

      let xPosition, yPosition, fill;
      if (width > textWidth + padding * 2) {
        // Label fits inside the bar
        xPosition = x + width / 2;
        yPosition = y + height / 2;
        fill = "hsl(var(--background))";
      } else {
        // Label doesn't fit, position it outside
        xPosition = x + width + padding;
        yPosition = y + height / 2;
        fill = "hsl(var(--foreground))";
      }

      return (
        <text
          x={xPosition}
          y={yPosition}
          fill={fill}
          textAnchor={width > textWidth + padding * 2 ? "middle" : "start"}
          dominantBaseline="central"
          fontSize={fontSize}
        >
          {formattedValue}
        </text>
      );
    };

    return (
      <div className="mb-6">
        <h3 className="mb-2 text-lg font-semibold text-primary">Key Ratios</h3>
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart data={alignedAnnual}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--chart-grid))" />
            <XAxis 
              dataKey="label" 
              stroke="hsl(var(--chart-axis))"
              tick={axisTickStyle}
            />
            <YAxis 
              yAxisId="left"
              stroke="hsl(var(--chart-axis))"
              tick={axisTickStyle}
              tickFormatter={formatPercentage}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              stroke="hsl(var(--primary))"
              tick={axisTickStyle}
              tickFormatter={formatPercentage}
            />
            <Tooltip content={customTooltip} />
            <Legend formatter={legendFormatter} />
            <Bar dataKey="return_on_average_assets" fill="hsl(var(--chart-2))" name="Return on Assets" yAxisId="left">
              <LabelList dataKey="return_on_average_assets" content={<CustomizedLabel />} />
            </Bar>
            <Bar dataKey="return_on_average_equity" fill="hsl(var(--chart-3))" name="Return on Equity" yAxisId="left">
              <LabelList dataKey="return_on_average_equity" content={<CustomizedLabel />} />
            </Bar>
            <Line 
              type="monotone" 
              dataKey="efficiency_ratio" 
              stroke="hsl(var(--chart-1))"
              name="Efficiency Ratio" 
              yAxisId="right"
              dot={false}
              strokeWidth={3}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
          <span className="ml-2 text-foreground">Loading Pro Forma Statements...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="relative rounded-xl border border-destructive/45 bg-destructive/15 px-4 py-3 text-destructive" role="alert">
          <strong className="font-bold">Error:</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      );
    }

    if (!annualSummary || annualSummary.length === 0) {
      console.warn('No data available for Pro Forma Statements:', { annualSummary });
      return <p className="text-foreground">No data available for Pro Forma Statements.</p>;
    }

    console.log('Key Ratios Data:', annualSummary.map(item => ({
      index: item.index,
      efficiency_ratio: item.efficiency_ratio,
      return_on_average_assets: item.return_on_average_assets,
      return_on_average_equity: item.return_on_average_equity
    })));

    return (
      <>
        {renderChart("Balance Sheet", [
          { dataKey: "loan_balance", color: "hsl(var(--chart-1))", name: "Loan Balance" },
          { dataKey: "line_balance", color: "hsl(var(--chart-3))", name: "Line Balance" },
          { dataKey: "deposit_balance", color: "hsl(var(--chart-2))", name: "Deposit Balance" }
        ], formatCurrency)}

        {renderCombinedChart()}

        {renderChart("Income Statement", [
          { dataKey: "interest_income_loans", color: "hsl(var(--chart-1))", name: "Interest Income (Loans)" },
          { dataKey: "interest_income_lines", color: "hsl(var(--chart-3))", name: "Interest Income (Lines)" },
          { dataKey: "interest_expense", color: "hsl(var(--chart-down))", name: "Interest Expense" },
          { dataKey: "non_interest_income", color: "hsl(var(--chart-2))", name: "Non-Interest Income" },
          { dataKey: "non_interest_expense", color: "hsl(var(--chart-5))", name: "Non-Interest Expense" },
          { dataKey: "net_income", color: "hsl(var(--chart-1))", name: "Net Income", dashed: true }
        ], formatCurrency)}

        {renderKeyRatiosChart()}
      </>
    );
  };

  return (
    <Card className="card-wrapper mt-6">
      <CardHeader>
        <CardTitle className="!text-primary">Pro Forma Statements</CardTitle>
      </CardHeader>
      <CardContent>
        {renderContent()}
      </CardContent>
    </Card>
  );
};

export default RMProFormaStatements;